import {
  holds,
  LANDED_PORTAL_SCREENS,
  type CapabilityKey,
  type CapabilitySnapshot,
  type TranslationKey,
} from "@planner/core";

/**
 * التنقّل **مبنيٌّ من الصلاحيات**، لا مكتوباً بيد.
 *
 * كل بند يعلن القدرة التي تفتحه، فالقائمة تُشتقّ من نفس المحرّك الذي يحرس
 * السيرفر. وبهذا يستحيل أن تظهر شاشةٌ في القائمة ثم تُرفض عند فتحها — وهي
 * تجربة v2 المعتادة: القائمة مكتوبة في مكان، والحراسة في مكان آخر، فيتفرّقان
 * مع أول تعديل.
 *
 * وبندٌ بلا قدرة (`capability: null`) يعني: **يراه كل من دخل**.
 */

export type NavGroup = "general" | "system" | "me";

export type NavItem = {
  readonly key: string;
  readonly labelKey: TranslationKey;
  readonly group: NavGroup;
  /**
   * القدرة التي تفتح هذا البند.
   *
   * و**قائمةٌ منها تعني «أيٌّ واحدة تكفي»** — لشاشةٍ تجمع أكثر من قائمة: «شغلي»
   * تجمع كروت العملاء والمهام المباشرة، فمن يحمل إحداهما له فيها شغلٌ يراه.
   *
   * ولا يجوز أن تكون **أضيق** من بوّابة الوجهة: في v2 كانت بوّابةُ التبويب أضيق
   * من بوّابة الشاشة، **فأدوارٌ لا طريقَ لها إلى شاشةٍ يجوز لها**. ولا **أوسع**:
   * بندٌ يفتح رفضاً أسوأ من بندٍ غائب.
   */
  readonly capability: CapabilityKey | readonly CapabilityKey[] | null;
  readonly icon: string;
  /** يظهر في الشريط السفلي على الهاتف — أربعة بالضبط، والباقي تحت «المزيد» */
  readonly primary?: true;
  /**
   * **نزلت شاشتُها فعلاً؟**
   *
   * والقاعدةُ مكتوبةٌ أسفلُ منذ المرحلة الأولى — «ولا بند لشاشة لم تنزل بعد» —
   * **وكانت تُخالَف في أربعة بنود**: السجلّ الجنائي، وصحة النظام، والملفّ
   * الشخصي، والرئيسية. كلُّها تفتح «الشاشة دي لسه منزلتش».
   *
   * فصارت القاعدةُ حقلاً: **ما لم ينزل لا يُرسَم**، ويبقى مُعلَناً هنا بمرحلته
   * فيُعرَف «ماذا بقي؟» — وبوابةٌ تكسر البناء لو أُعلن نازلاً بلا شاشة.
   */
  readonly landed: boolean;
  /** المرحلةُ التي ينزل فيها — لمن يسأل عمّا بقي، لا نصٌّ يُعرَض لمستخدم */
  readonly phase?: string;
};

export const NAV_GROUPS: readonly NavGroup[] = ["general", "system", "me"];

export const NAV_GROUP_LABEL: Record<NavGroup, TranslationKey> = {
  general: "nav.group.general",
  system: "nav.group.system",
  me: "nav.group.me",
};

/**
 * بنود المرحلة الأولى.
 *
 * **ولا بند لشاشة لم تنزل بعد.** بندٌ يفتح فراغاً أسوأ من بندٍ غائب: الأول
 * يبدو عطباً، والثاني يبدو نظاماً ينمو.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: "home",
    landed: true,
    labelKey: "nav.home",
    group: "general",
    capability: null,
    icon: "home",
    primary: true,
  },
  {
    key: "tasks",
    landed: true,
    labelKey: "nav.tasks",
    group: "general",
    capability: "tasks.view",
    icon: "check",
  },
  /**
   * **«اللي عليك» — ونزلت شاشتُه في المرحلة الحادية عشرة.**
   *
   * **وكان بلا بندٍ بقصد**: «القدرة موجودة والموديول لم ينزل، فكان البند يأخذ
   * خانة في شريط الهاتف ويفتح لسه منزلتش». **فنزل سطرُه مع شاشته لا قبلها.**
   *
   * **وفي الشريط السفلي** — فهو أوّلُ ما يُفتَح كلَّ صباح: **ما عليك اليوم**.
   */
  {
    key: "actions",
    landed: true,
    labelKey: "nav.actions",
    group: "general",
    capability: "actions.view_own",
    icon: "check",
    primary: true,
  },
  /**
   * **«شغلي» تجمع قائمتين، فبوّابتُها اتحادُ قدرتيهما.**
   *
   * وهي أوّل ما يفتحه الموظّف صباحاً، فتأخذ خانةً في شريط الهاتف. وكتبتُها أوّلاً
   * **بلا قدرة** — «شغلُك تراه» — فأمسكها اختبار القائمة: من لا يحمل شيئاً كان
   * يرى بنداً **يفتح رفضاً**، وهو ما تمنعه هذه الوحدة صراحةً. فصارت بقدرتيها:
   * من يحمل إحداهما له فيها شغل، ومن لا يحمل أيّاً منهما لا يرى البند.
   */
  {
    key: "my-work",
    landed: true,
    labelKey: "nav.myWork",
    group: "general",
    capability: ["cards.view", "tasks.view"],
    icon: "check",
  },
  {
    key: "clients",
    landed: true,
    labelKey: "nav.clients",
    group: "general",
    capability: "clients.view",
    icon: "building",
  },
  /**
   * **الخطط** — ودورة الاعتماد.
   *
   * وبندُها يظهر لمن يحمل رؤية الخطط وحده: «بندٌ يفتح فراغاً أسوأ من بندٍ غائب».
   */
  {
    key: "plans",
    landed: true,
    labelKey: "nav.plans",
    group: "general",
    capability: "plans.view",
    icon: "calendar",
    primary: true,
  },
  {
    key: "boards",
    landed: true,
    labelKey: "nav.boards",
    group: "general",
    capability: "boards.view",
    icon: "list",
  },
  /**
   * **والشاتُ بندٌ عامّ** — فأدنى قدرةٍ فيه يحملها كلُّ من يكتب.
   *
   * **ولا يدخل الشريطَ السفلي**: أربعةٌ فيه، والقرارُ أيُّها يُطوى لا أن يُضاف
   * خامسٌ يضيق. ويبقى شاشةً في «المزيد» — **وشاشةٌ تُفتَح خيرٌ من بندٍ يزحم**.
   */
  {
    key: "chat",
    landed: true,
    labelKey: "nav.chat",
    group: "general",
    capability: "chat.send",
    icon: "message",
    primary: true,
  },
  /** **والاجتماعاتُ بندٌ لمن يراها** — بقدرة القراءة، لا بقدرة البتّ */
  {
    key: "meetings",
    landed: true,
    labelKey: "nav.meetings",
    group: "general",
    capability: "meetings.view",
    icon: "calendar",
  },
  {
    key: "notifications",
    landed: true,
    labelKey: "nav.notifications",
    group: "general",
    capability: "notifications.view_own",
    icon: "bell",
  },
  {
    key: "access",
    landed: true,
    labelKey: "nav.access",
    group: "general",
    capability: "access_requests.create",
    icon: "key",
  },
  {
    key: "people",
    landed: true,
    labelKey: "nav.people",
    group: "system",
    capability: "identity.users.view",
    icon: "user",
  },
  {
    key: "roles",
    landed: true,
    labelKey: "nav.roles",
    group: "system",
    capability: "roles.view",
    icon: "shield",
  },
  {
    key: "departments",
    landed: true,
    labelKey: "nav.departments",
    group: "system",
    capability: "org.departments.view",
    icon: "building",
  },
  /**
   * **ناحيةُ التخزين** — واليتامى أوّلُ ما فيها.
   *
   * ولم تكن موجودة، فكان جدولُ اليتامى **بلا شاشةٍ تعرضه** — وهي بعينها
   * شكوى v2 التي بُنيت المصالحة لإغلاقها.
   */
  {
    key: "reports",
    landed: true,
    labelKey: "nav.reports",
    group: "general",
    capability: "reports.view",
    icon: "chart",
  },
  /**
   * **والتحليلات** — المرحلة العاشرة.
   *
   * وقدرتُها `analytics.own.view` **ويحملها كلُّ موظّف**: فمن يُقاس يحقّ له أن
   * يرى بماذا يُقاس. ولو كانت قدرةَ الشركة **لاختفى البندُ عن كل الفريق** وهم
   * أصحابُ الأرقام، وهو بعينه ما يجعل شاشةً تبدو للإدارة وحدها.
   */
  {
    key: "analytics",
    landed: true,
    labelKey: "nav.analytics",
    group: "general",
    capability: "analytics.own.view",
    icon: "chart",
  },
  {
    /** **وطابورُ النشر بين الشغل والنظام** — فهو شغلٌ يوميٌّ لا إدارة */
    key: "moderation",
    landed: true,
    labelKey: "nav.moderation",
    group: "general",
    capability: "moderation.queue.view",
    /** **ومن الأيقونات الموجودة** — فاسمٌ مخترَعٌ لا يُرسَم شيئاً */
    icon: "message",
  },
  {
    key: "storage",
    landed: true,
    labelKey: "nav.storage",
    group: "system",
    capability: "clients.storage.manage",
    icon: "database",
  },
  /**
   * **ونزلت شاشتاهما في المرحلة الحادية عشرة** — وكانا مُعلَنين مؤجَّلَين منذ
   * الأولى: البندُ لا يُرسَم حتى تُفتَح شاشتُه، **فلا بندَ يفتح «لسه منزلتش»**.
   */
  {
    key: "health",
    landed: true,
    labelKey: "nav.health",
    group: "system",
    capability: "platform.health.view",
    icon: "pulse",
  },
  {
    key: "audit",
    landed: true,
    labelKey: "nav.audit",
    group: "system",
    capability: "platform.audit.view",
    icon: "list",
  },
  /**
   * **وسجلُّ التغييرات بندٌ لمن يكتبه** — والنافذةُ تصل الفريقَ كلَّه.
   *
   * فالبندُ بقدرة الإدارة، **و«إيه الجديد» نافذةٌ في القوقعة لكل من دخل** —
   * فلا يحتاج الفريقُ بنداً يفتح قائمةً يُرفَض عليها.
   */
  {
    key: "changelog",
    landed: true,
    labelKey: "nav.changelog",
    group: "system",
    capability: "platform.changelog.manage",
    icon: "book",
  },
  {
    key: "notifications-admin",
    landed: true,
    labelKey: "nav.notificationsAdmin",
    group: "system",
    capability: "platform.notifications.manage",
    icon: "bell",
  },
  {
    key: "settings",
    landed: true,
    labelKey: "nav.settings",
    group: "system",
    capability: "platform.settings.manage",
    icon: "sliders",
  },
  {
    key: "errors",
    landed: true,
    labelKey: "nav.errorCatalog",
    group: "system",
    capability: "platform.health.view",
    icon: "book",
  },
  {
    key: "profile",
    landed: true,
    labelKey: "nav.profile",
    group: "me",
    capability: "identity.profile.manage_own",
    icon: "user",
  },
];

/**
 * **بنودُ البورتال — مبنيّةٌ من سجلّ شاشاته، لا مكتوبةً ثانيةً.**
 *
 * فالسجلُّ يعرف ما نزل وما أُجِّل وبموديول أيّهما، **والبوابةُ تقرأ منه**. ولو
 * كُتبت البنودُ بيدٍ هنا، لصار الوعدُ بالعودة ملاحظةً في موضعين — وأحدُهما
 * يُنسى. **ولا بندَ لشاشةٍ لم تنزل**: «بندٌ يفتح فراغاً أسوأ من بندٍ غائب».
 */
export const PORTAL_NAV_ITEMS: readonly NavItem[] = LANDED_PORTAL_SCREENS.map((screen) => ({
  key: screen.key,
  labelKey: screen.labelKey,
  group: "general" as NavGroup,
  capability: screen.capability,
  icon: screen.icon,
  /** نازلةٌ بالتعريف — `LANDED_PORTAL_SCREENS` لا تحمل غيرَها */
  landed: true,
  ...(screen.primary ? { primary: screen.primary } : {}),
}));

/** ما يراه هذا المستخدم بالذات — يُحسب مرة واحدة في القوقعة، لا في كل رسم */
export function visibleNav(
  snapshot: CapabilitySnapshot,
  items: readonly NavItem[] = NAV_ITEMS,
): readonly NavItem[] {
  return items.filter((item) => {
    /**
     * **وما لم تنزل شاشتُه لا يُرسَم** — القاعدةُ المكتوبة أعلاه، مفروضةً.
     *
     * «بندٌ يفتح فراغاً أسوأ من بندٍ غائب: الأول يبدو عطباً، والثاني يبدو
     * نظاماً ينمو». وكانت تُخالَف في أربعة بنود منذ المرحلة الأولى.
     */
    if (!item.landed) return false;
    if (item.capability === null) return true;
    /** قائمةٌ تعني «أيٌّ واحدة تكفي» — بـ`holds` نفسها، لا بفحصٍ ثانٍ */
    const needed = Array.isArray(item.capability) ? item.capability : [item.capability];
    return needed.some((capability) => holds(snapshot, capability));
  });
}

/**
 * شريط الهاتف: أربعة بنود في البورتال، وما زاد يذهب إلى «المزيد».
 *
 * أربعة لا خمسة — هدفُ اللمس على شاشة ٣٦٠ بكسل يضيق بعدها، والشريط السفلي
 * هو الطريق الوحيد للتنقّل على الهاتف.
 */
export const MOBILE_SLOTS = 4;

/**
 * **وشريطُ الموظف خمسة** — الرئيسية · اللي عليك · الخطط · الشات · المزيد، بموك أب
 * الرئيسية المعتمَد (شادي ٢٠٢٦-١٠-٢٦). وكلُّ وجهةٍ أخرى تحت «المزيد» — لا يسقط منها شيء.
 */
export const STAFF_MOBILE_SLOTS = 5;

export function splitForMobile(
  items: readonly NavItem[],
  slots: number = MOBILE_SLOTS,
): {
  readonly bar: readonly NavItem[];
  readonly more: readonly NavItem[];
} {
  const primary = items.filter((i) => i.primary === true);
  const rest = items.filter((i) => i.primary !== true);
  /** **المُعلَنُ أوّلاً، ثم ما يملأ الخانات** — فمن لا يملك الخطط لا يرى خانةً فارغة */
  const ordered = [...primary, ...rest];
  // لا خانة تُهدر على «المزيد» حين لا شيء تحته
  if (ordered.length <= slots) return { bar: ordered, more: [] };
  return { bar: ordered.slice(0, slots - 1), more: ordered.slice(slots - 1) };
}
